import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { getMyCustomer, getWarehouses, getProductCodes, getOrderById, saveCustomerOrder, addOrderAdditionalFile, removeOrderAdditionalFile, parsePurchaseOrder } from '../../../api/ordersApi.js';
import { useRetryFetch } from '../hooks/useRetryFetch.js';
import AddProductModal from '../components/AddProductModal.jsx';
import Dropdown from '../components/Dropdown.jsx';
import Spinner from '../components/Spinner.jsx';
import WarningIcon from '../components/WarningIcon.jsx';
import Modal from '../components/Modal.jsx';
import ErrorModal from '../components/ErrorModal.jsx';
import Tooltip from '../components/Tooltip.jsx';
import RichTextEditor from '../../../components/RichTextEditor.js';
import { formatCurrency } from '../utils/format.js';
import { parseOrderApiError, classifyPortalError } from '../utils/apiError.js';
import { showErrorToast, showSuccessToast } from '../utils/toast.js';
import { PHONE_REGEX, EMAIL_REGEX } from '../../../utils/constants.js';
import '../components/CommentModal.css';
import './NewOrder.css';

const MAX_PO_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ADDITIONAL_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ADDITIONAL_FILES = 10;

const ADDITIONAL_FILE_MIMES = [
  // Documents
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/rtf",
  "text/plain",

  // Images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff",
  "image/svg+xml",

  // Data
  "text/csv",
  "application/json",
];

const ADDITIONAL_FILE_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx",
  ".odt", ".ods", ".rtf", ".txt",
  ".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".tiff", ".tif", ".svg",
  ".csv", ".json",
];

function validateAdditionalFile(file) {
  const ext = `.${(file.name.split('.').pop() || '').toLowerCase()}`;
  const typeAllowed = ADDITIONAL_FILE_MIMES.includes(file.type) || ADDITIONAL_FILE_EXTENSIONS.includes(ext);
  if (!typeAllowed) {
    return `${file.name}: this file type is not allowed`;
  }
  if (file.size > MAX_ADDITIONAL_FILE_SIZE) {
    return `${file.name}: file size exceeds 5MB`;
  }
  return null;
}

const emptyForm = {
  deliveryAddressId: '',
  purchaseOrderDate: '',
  deliveryContactId: '',
  deliveryInstruction: '',
  deliveryContactEmail: '',
  requiredDate: '',
  phoneNo: '',
  warehouseCode: '',
  purchaseOrderId: '',
};

function orderToFormFields(order) {
  return {
    deliveryAddressId: order.deliveryAddress?.id ? String(order.deliveryAddress.id) : '',
    purchaseOrderDate: order.purchaseOrderDate ? order.purchaseOrderDate.split('T')[0] : '',
    deliveryContactId: order.deliveryContact?.id ? String(order.deliveryContact.id) : '',
    deliveryInstruction: order.deliveryInstruction || '',
    deliveryContactEmail: order.email || order.deliveryContact?.email || '',
    requiredDate: order.requiredDate ? order.requiredDate.split('T')[0] : '',
    phoneNo: order.phoneNo || order.deliveryContact?.phoneNo || '',
    warehouseCode: order.warehouseCode || '',
    purchaseOrderId: order.purchaseOrderId || '',
  };
}

function orderToProducts(order, { keepIds }) {
  return (order.products || []).map((p) => {
    const unitPrice = Number(p.customerPrice ?? p.unitPrice ?? 0);
    const quantity = Number(p.quantity) || 0;
    const savedTotal = p.finalPrice ?? p.totalPrice ?? p.total;
    return {
      ...(keepIds && p.id ? { orderProductId: p.id } : {}),
      productCode: p.productCode,
      description: p.description || p.productDescription || '',
      quantity,
      rrp: p.rrp,
      unitPrice,
      totalPrice: savedTotal != null ? Number(savedTotal) : Number((quantity * unitPrice).toFixed(2)),
      comments: p.comments || p.productComments || '',
    };
  });
}

export default function NewOrder() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(orderId);
  const duplicateOrder = location.state?.duplicateOrder;

  const [customer, setCustomer] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [productCodes, setProductCodes] = useState([]);
  const [productCodesLoading, setProductCodesLoading] = useState(false);
  const productCodesRequestedRef = useRef(false);

  const [form, setForm] = useState(emptyForm);
  const [products, setProducts] = useState([]);
  const [purchaseOrderFile, setPurchaseOrderFile] = useState(null);
  const [existingPoFileName, setExistingPoFileName] = useState('');
  const [poFileError, setPoFileError] = useState('');
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [existingAdditionalFiles, setExistingAdditionalFiles] = useState([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comment, setComment] = useState('');
  const [draftComment, setDraftComment] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProductIndex, setEditingProductIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fileActionMessage, setFileActionMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [orderStatus, setOrderStatus] = useState('');

  const isPendingEdit = isEditMode && String(orderStatus || '').toLowerCase() === 'pending';

  useEffect(() => {
    setForm(emptyForm);
    setProducts([]);
    setPurchaseOrderFile(null);
    setExistingPoFileName('');
    setPoFileError('');
    setAdditionalFiles([]);
    setExistingAdditionalFiles([]);
    setComment('');
    setFieldErrors({});
    setApiError(null);
    setProductCodes([]);
    setProductCodesLoading(false);
    productCodesRequestedRef.current = false;
    setWarehouses([]);
    setOrderStatus('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, location.key]);

  const { data: loaded, loading, error, errorMessage, isEntityNotFound, manualRetries, retry } = useRetryFetch(
    () => getMyCustomer().then(async (me) => {
      const order = isEditMode ? await getOrderById(orderId) : null;
      return { me, order };
    }),
    [orderId, location.key]
  );

  // Decoupled from the customer/order fetch above so the Delivery Address and
  // Delivery Contact dropdowns (fed by `customer`) aren't held up waiting on
  // warehouses — that dropdown gets its own small loading state instead.
  const {
    data: warehouseData,
    loading: warehousesLoading,
    error: warehousesError,
    errorMessage: warehousesErrorMessage,
  } = useRetryFetch(
    () => (customer?.id ? getWarehouses(customer.id) : Promise.resolve(null)),
    [customer?.id, orderId, location.key]
  );

  useEffect(() => {
    if (!warehousesError) return;
    showErrorToast(warehousesErrorMessage || "Couldn't load warehouses. Please try again.");
  }, [warehousesError, warehousesErrorMessage]);

  useEffect(() => {
    if (!loaded) return;
    setCustomer(loaded.me);

    if (isEditMode && loaded.order) {
      setForm((f) => ({ ...f, ...orderToFormFields(loaded.order) }));
      setProducts(orderToProducts(loaded.order, { keepIds: true }));
      setComment(loaded.order.config || '');
      setExistingPoFileName(loaded.order.purchaseOrderFileName || '');
      setExistingAdditionalFiles(loaded.order.additionalFiles || []);
      setOrderStatus(loaded.order.status || '');
    } else if (duplicateOrder) {
      setForm((f) => ({ ...f, ...orderToFormFields(duplicateOrder) }));
      setProducts(orderToProducts(duplicateOrder, { keepIds: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    if (!warehouseData) return;
    setWarehouses(warehouseData);
    if (!isEditMode && !duplicateOrder) {
      const mainWh = warehouseData.find((w) => String(w.warehouseCode).toUpperCase() === 'MAIN');
      setForm((f) => ({ ...f, warehouseCode: mainWh?.warehouseCode || warehouseData[0]?.warehouseCode || '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseData]);

  const updateField = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setFieldErrors((fe) => ({ ...fe, [field]: undefined }));
  };

  const handleContactChange = (id) => {
    const contact = (customer?.deliveryContacts || []).find((c) => String(c.id) === id);
    setForm((f) => ({
      ...f,
      deliveryContactId: id,
      deliveryContactEmail: contact?.email ?? '',
      phoneNo: contact?.phoneNo ?? f.phoneNo,
    }));
    setFieldErrors((fe) => ({ ...fe, deliveryContactId: undefined, deliveryContactEmail: undefined }));
  };

  const handlePoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setPoFileError('Only PDF files are allowed');
      return;
    }
    if (file.size > MAX_PO_FILE_SIZE) {
      setPoFileError('File size exceeds 5MB. Please upload a smaller PDF file.');
      return;
    }
    setPoFileError('');
    setPurchaseOrderFile(file);
    setExistingPoFileName('');
    setFileActionMessage('Parsing purchase order…');
    try {
      const data = await parsePurchaseOrder(file);

      if (Array.isArray(data.warnings) && data.warnings.length > 0) {
        showErrorToast(data.warnings.join(' '));
        return;
      }

      setForm((f) => ({
        ...f,
        purchaseOrderId: data.purchaseOrderId ?? data.id ?? f.purchaseOrderId,
        deliveryInstruction: data.deliveryInstruction ?? f.deliveryInstruction,
        requiredDate: data.requiredDate ? String(data.requiredDate).split('T')[0] : f.requiredDate,
        purchaseOrderDate: data.purchaseOrderDate ? String(data.purchaseOrderDate).split('T')[0] : f.purchaseOrderDate,
        phoneNo: data.phoneNo ?? f.phoneNo,
        deliveryContactEmail: data.email ?? f.deliveryContactEmail,
      }));
      setFieldErrors((fe) => ({
        ...fe,
        purchaseOrderId: undefined,
        deliveryAddressId: undefined,
        deliveryContactId: undefined,
      }));

      if (data.deliveryAddress?.id || data.deliveryContact?.id) {
        setCustomer((prev) => {
          if (!prev) return prev;
          const addresses = prev.addresses || [];
          const deliveryContacts = prev.deliveryContacts || [];
          const nextAddresses =
            data.deliveryAddress?.id && !addresses.some((a) => String(a.id) === String(data.deliveryAddress.id))
              ? [...addresses, data.deliveryAddress]
              : addresses;
          const nextContacts =
            data.deliveryContact?.id && !deliveryContacts.some((c) => String(c.id) === String(data.deliveryContact.id))
              ? [...deliveryContacts, data.deliveryContact]
              : deliveryContacts;
          return { ...prev, addresses: nextAddresses, deliveryContacts: nextContacts };
        });
      }
      if (data.deliveryAddress?.id) {
        setForm((f) => ({ ...f, deliveryAddressId: String(data.deliveryAddress.id) }));
      }
      if (data.deliveryContact?.id) {
        setForm((f) => ({ ...f, deliveryContactId: String(data.deliveryContact.id) }));
      }

      if (Array.isArray(data.products) && data.products.length > 0) {
        const mappedProducts = data.products.map((p) => {
          const unitPrice = Number(p.customerPrice ?? p.unitPrice ?? 0);
          const quantity = Number(p.quantity) || 0;
          return {
            productCode: p.productCode,
            description: p.description || p.productDescription || '',
            quantity,
            rrp: Number(p.rrp) || 0,
            unitPrice,
            totalPrice: Number((quantity * unitPrice).toFixed(2)),
            comments: p.comments || p.productComments || '',
          };
        });
        setProducts(mappedProducts);
        setFieldErrors((fe) => ({ ...fe, products: undefined }));
      }

      showSuccessToast('Purchase order parsed successfully');
    } catch (err) {
      showErrorToast(classifyPortalError(err).message || "Couldn't parse the purchase order file.");
    } finally {
      setFileActionMessage(null);
    }
  };

  const loadProductCodes = () => {
    if (productCodesRequestedRef.current || !customer?.id) return;
    productCodesRequestedRef.current = true;
    setProductCodesLoading(true);
    getProductCodes(customer.id)
      .then(setProductCodes)
      .catch((err) => {
        productCodesRequestedRef.current = false;
        showErrorToast(classifyPortalError(err).message || "Couldn't load products. Please try again.");
        setShowAddProduct(false);
      })
      .finally(() => setProductCodesLoading(false));
  };

  const handleAddAdditionalFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const validFiles = [];
    for (const file of files) {
      const validationError = validateAdditionalFile(file);
      if (validationError) {
        showErrorToast(validationError);
      } else {
        validFiles.push(file);
      }
    }
    if (!validFiles.length) return;

    const currentTotal = existingAdditionalFiles.length + additionalFiles.length;
    const remainingSlots = MAX_ADDITIONAL_FILES - currentTotal;
    let filesToAdd = validFiles;
    if (remainingSlots <= 0) {
      showErrorToast(`You can add a maximum of ${MAX_ADDITIONAL_FILES} files. Delete an existing file to add more.`);
      return;
    }
    if (validFiles.length > remainingSlots) {
      filesToAdd = validFiles.slice(0, remainingSlots);
      showErrorToast(
        `Only ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} can be added (max ${MAX_ADDITIONAL_FILES} total). Delete an existing file to add more.`
      );
    }

    if (isEditMode) {
      setFileActionMessage('Uploading file…');
      try {
        for (const file of filesToAdd) {
          await addOrderAdditionalFile(orderId, file);
          setExistingAdditionalFiles((prev) => [...prev, { fileName: file.name }]);
        }
      } catch (err) {
        showErrorToast(classifyPortalError(err).message || "Couldn't upload the file. Please try again.");
      } finally {
        setFileActionMessage(null);
      }
      return;
    }

    setAdditionalFiles((prev) => [...prev, ...filesToAdd]);
  };

  const removeAdditionalFile = (index) => {
    setAdditionalFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingAdditionalFile = async (fileName) => {
    setFileActionMessage('Removing file…');
    try {
      await removeOrderAdditionalFile(orderId, fileName);
      setExistingAdditionalFiles((prev) =>
        prev.filter((f) => (typeof f === 'string' ? f : (f.fileName ?? f.name)) !== fileName)
      );
    } catch (err) {
      showErrorToast(classifyPortalError(err).message || "Couldn't remove the file. Please try again.");
    } finally {
      setFileActionMessage(null);
    }
  };

  const openCommentModal = () => {
    setDraftComment(comment);
    setShowCommentModal(true);
  };

  const saveComment = () => {
    setComment(draftComment.trim());
    setShowCommentModal(false);
  };

  const handleSaveProduct = (product) => {
    setProducts((prev) => {
      if (editingProductIndex == null) return [...prev, product];
      return prev.map((p, i) => (i === editingProductIndex ? { ...p, ...product } : p));
    });
    setShowAddProduct(false);
    setEditingProductIndex(null);
    setFieldErrors((fe) => ({ ...fe, products: undefined }));
  };

  const openAddProduct = () => {
    setEditingProductIndex(null);
    setShowAddProduct(true);
  };

  const openEditProduct = (idx) => {
    setEditingProductIndex(idx);
    setShowAddProduct(true);
  };

  const closeProductModal = () => {
    setShowAddProduct(false);
    setEditingProductIndex(null);
  };

  const removeProduct = (idx) => setProducts((prev) => prev.filter((_, i) => i !== idx));

  const productsTotal = products.reduce((sum, p) => sum + p.totalPrice, 0);
  const totalAdditionalFiles = existingAdditionalFiles.length + additionalFiles.length;
  const additionalFilesAtCap = totalAdditionalFiles >= MAX_ADDITIONAL_FILES;

  const buildPayload = () => ({
    ...(isEditMode && { id: orderId }),
    customerId: customer.id,
    name: `Order-${Date.now()}`,
    deliveryAddressId: form.deliveryAddressId ? Number(form.deliveryAddressId) : null,
    deliveryContactId: form.deliveryContactId ? Number(form.deliveryContactId) : null,
    deliveryName: (customer?.deliveryContacts || []).find((c) => String(c.id) === form.deliveryContactId)?.name || null,
    phoneNo: form.phoneNo || null,
    email: form.deliveryContactEmail || null,
    requiredDate: form.requiredDate || null,
    invoiceDate: null,
    dueDate: null,
    paymentTerm: null,
    salesOrderGroup: null,
    salesPerson: null,
    warehouseCode: form.warehouseCode || 'MAIN',
    purchaseOrderId: form.purchaseOrderId || null,
    purchaseOrderDate: form.purchaseOrderDate || new Date().toISOString().slice(0, 10),
    purchaseOrderTotal: productsTotal,
    deliveryInstruction: form.deliveryInstruction?.trim() ? form.deliveryInstruction.trim() : null,
    config: comment || null,
    products: products.map((p) => ({
      ...(isEditMode && p.orderProductId ? { id: p.orderProductId } : {}),
      productCode: p.productCode,
      quantity: Number(p.quantity),
      rrp: Number(p.rrp) || 0,
      customerPrice: Number(p.unitPrice),
      comments: p.comments || null,
      finalPrice: Number(p.totalPrice),
      ...(isEditMode && { isUpdated: true }),
    })),
  });

  // Purchase Order file / total are intentionally NOT validated here — the
  // backend decides whether the file is required (e.g. per-customer
  // isPOFileRequired) and reports it back via an InvalidRequestBodyError.
  const validateFields = (isSaveOnly) => {
    const errors = {};
    if (!customer?.id) errors.customer = 'This is required';
    if (!isSaveOnly) {
      if (!form.deliveryAddressId) errors.deliveryAddressId = 'This is required';
      if (!form.deliveryContactId) errors.deliveryContactId = 'This is required';
      if (!form.purchaseOrderId?.trim()) errors.purchaseOrderId = 'This is required';
      if (!products.length) errors.products = 'Add at least one product';
    }
    if (form.deliveryContactEmail?.trim() && !EMAIL_REGEX.test(form.deliveryContactEmail.trim())) {
      errors.deliveryContactEmail = 'Enter a valid email address.';
    }
    return errors;
  };

  const handleSubmit = async (isSaveOnly) => {
    const errors = validateFields(isSaveOnly);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      await saveCustomerOrder(buildPayload(), {
        orderId: isEditMode ? orderId : undefined,
        purchaseOrderFile,
        additionalFiles,
        isSave: isSaveOnly,
      });
      showSuccessToast(
        isSaveOnly
          ? 'Order saved successfully'
          : isEditMode
            ? 'Order updated successfully'
            : 'Order created successfully'
      );
      navigate('/customer/orders');
    } catch (err) {
      setApiError(parseOrderApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="customer-portal-page-content">
      <div className="customer-portal-new-order-header">
        <div>
          <div className="customer-portal-orders-header__breadcrumb">
            <Link to="/customer/orders">My Orders</Link>
            <Crumb />
            <span>{isEditMode ? 'Edit Order' : 'New Order'}</span>
          </div>
          <div className="customer-portal-orders-header__title">{isEditMode ? 'Edit Order' : 'New Order'}</div>
          <div className="customer-portal-new-order-header__subtitle">
            {isEditMode ? 'Update your purchase order' : 'Create a new purchase order'}
          </div>
        </div>
        <div className="customer-portal-new-order-header__actions">
          <button type="button" className="customer-portal-btn customer-portal-btn--ghost" onClick={openCommentModal}>
            {comment ? 'Edit Comment' : 'Add Comments'}
          </button>
          <div className="customer-portal-new-order-po-upload">
            <label htmlFor="customer-po-file-input" className="customer-portal-btn customer-portal-btn--ghost customer-portal-new-order-upload-btn">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <Tooltip as="span" className="customer-portal-new-order-upload-btn__label">
                {purchaseOrderFile ? purchaseOrderFile.name : (existingPoFileName || 'Upload Purchase Order File')}
              </Tooltip>
              {(purchaseOrderFile || existingPoFileName) && (
                <button
                  type="button"
                  className="customer-portal-new-order-upload-btn__remove"
                  aria-label="Remove purchase order file"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPurchaseOrderFile(null);
                    setExistingPoFileName('');
                    setPoFileError('');
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              <input id="customer-po-file-input" type="file" accept="application/pdf" hidden onChange={handlePoFileChange} />
            </label>
          </div>
        </div>
      </div>

      {poFileError && <div className="customer-portal-new-order-error">{poFileError}</div>}

      <div className="customer-portal-card customer-portal-new-order-sales">
        <div className="customer-portal-new-order-sales__header">
          <div className="customer-portal-new-order-sales__title">Sales Order</div>
          <div className="customer-portal-new-order-sales__chips">
            <div className="customer-portal-new-order-chip">
              <span>CUSTOMER ENTITY:</span>
              <Tooltip as="strong">{customer?.entity || '—'}</Tooltip>
            </div>
            <div className="customer-portal-new-order-chip">
              <span>CUSTOMER CODE:</span>
              <Tooltip as="strong">{customer?.customerCode || '—'}</Tooltip>
            </div>
          </div>
        </div>

        <div className="customer-portal-new-order-sales__subheading">Customer Information</div>

        <div className="customer-portal-new-order-grid">
          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Customer Name</span>
            <div className={`customer-portal-new-order-readonly${fieldErrors.customer ? ' is-error' : ''}`}>
              <span className="customer-portal-new-order-readonly__avatar">
                {(customer?.name || '?').charAt(0).toUpperCase()}
              </span>
              {customer?.name || '—'}
            </div>
            <FieldError message={fieldErrors.customer} />
          </label>
          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Delivery Address</span>
            <Dropdown
              value={form.deliveryAddressId}
              onChange={(v) => {
                setForm((f) => ({ ...f, deliveryAddressId: v }));
                setFieldErrors((fe) => ({ ...fe, deliveryAddressId: undefined }));
              }}
              placeholder="Select Address"
              noDataText="No addresses found"
              options={(customer?.addresses || []).map((a) => ({
                value: String(a.id),
                label: [a.line1, a.street, a.city, a.postalCode, a.country].filter(Boolean).join(', '),
              }))}
              searchable
              searchPlaceholder="Search addresses…"
              showValueTooltip
              showOptionTooltip
              error={Boolean(fieldErrors.deliveryAddressId)}
            />
            <FieldError message={fieldErrors.deliveryAddressId} />
          </label>
          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Purchase Order Date</span>
            <input type="date" value={form.purchaseOrderDate} onChange={updateField('purchaseOrderDate')} />
          </label>

          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Delivery Contact</span>
            <Dropdown
              value={form.deliveryContactId}
              onChange={handleContactChange}
              placeholder="Select Contact"
              noDataText="No contacts found"
              options={(customer?.deliveryContacts || []).map((c) => ({ value: String(c.id), label: c.name }))}
              searchable
              searchPlaceholder="Search contacts…"
              showValueTooltip
              showOptionTooltip
              error={Boolean(fieldErrors.deliveryContactId)}
            />
            <FieldError message={fieldErrors.deliveryContactId} />
          </label>
          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Delivery Instruction</span>
            <input
              type="text"
              placeholder="Enter Delivery Instruction"
              value={form.deliveryInstruction}
              onChange={updateField('deliveryInstruction')}
            />
          </label>
          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Delivery Contact Email</span>
            <input
              type="email"
              placeholder="Enter email address"
              value={form.deliveryContactEmail}
              onChange={updateField('deliveryContactEmail')}
              className={fieldErrors.deliveryContactEmail ? 'is-error' : ''}
            />
            <FieldError message={fieldErrors.deliveryContactEmail} />
          </label>

          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Required Date</span>
            <input type="date" value={form.requiredDate} onChange={updateField('requiredDate')} />
          </label>
          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Phone No</span>
            <input
              type="text"
              placeholder="Enter phone number"
              value={form.phoneNo}
              onChange={(e) => {
                if (PHONE_REGEX.test(e.target.value)) {
                  setForm((f) => ({ ...f, phoneNo: e.target.value }));
                }
              }}
            />
          </label>
          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Warehouse</span>
            <Dropdown
              value={form.warehouseCode}
              onChange={(v) => setForm((f) => ({ ...f, warehouseCode: v }))}
              placeholder="Select Warehouse"
              noDataText={warehousesError ? "Couldn't load warehouses" : 'No warehouses found'}
              options={warehouses.map((w) => ({
                value: w.warehouseCode,
                label: w.warehouseName ? `${w.warehouseCode} - ${w.warehouseName}` : w.warehouseCode,
              }))}
              searchable
              searchPlaceholder="Search warehouses…"
              showValueTooltip
              showOptionTooltip
              loading={warehousesLoading}
              loadingText="Loading warehouses…"
            />
          </label>

          <label className="customer-portal-form-field">
            <span className="customer-portal-form-field__label">Purchase Order ID</span>
            <input
              type="text"
              className={fieldErrors.purchaseOrderId ? 'is-error' : ''}
              placeholder="Enter Purchase Order Id"
              value={form.purchaseOrderId}
              onChange={(e) => {
                updateField('purchaseOrderId')(e);
                setFieldErrors((fe) => ({ ...fe, purchaseOrderId: undefined }));
              }}
            />
            <FieldError message={fieldErrors.purchaseOrderId} />
          </label>
        </div>
      </div>

      <div className="customer-portal-card customer-portal-new-order-section">
        <div className="customer-portal-new-order-section__title">Documents</div>
        <label
          className={`customer-portal-btn customer-portal-btn--ghost customer-portal-new-order-choose-file${additionalFilesAtCap ? ' customer-portal-btn--disabled' : ''}`}
        >
          Choose File
          <input
            type="file"
            hidden
            multiple
            disabled={additionalFilesAtCap}
            accept={[...ADDITIONAL_FILE_MIMES, ...ADDITIONAL_FILE_EXTENSIONS].join(',')}
            onChange={handleAddAdditionalFiles}
          />
        </label>
        <div className="customer-portal-new-order-section__file-count">
          {totalAdditionalFiles}/{MAX_ADDITIONAL_FILES} files
          {additionalFilesAtCap && ' — delete a file to add more'}
        </div>
        {existingAdditionalFiles.length === 0 && additionalFiles.length === 0 ? (
          <div className="customer-portal-new-order-section__empty">No additional files uploaded</div>
        ) : (
          <ul className="customer-portal-new-order-doc-list">
            {existingAdditionalFiles.map((file, idx) => {
              const fileName = typeof file === 'string' ? file : (file.fileName ?? file.name);
              return (
                <li key={`existing-${idx}`}>
                  <Tooltip as="span">{fileName}</Tooltip>
                  <button
                    type="button"
                    className="customer-portal-icon-btn customer-portal-icon-btn--pink"
                    aria-label="Remove file"
                    onClick={() => removeExistingAdditionalFile(fileName)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </li>
              );
            })}
            {additionalFiles.map((file, idx) => (
              <li key={`${file.name}-${idx}`}>
                <Tooltip as="span">{file.name}</Tooltip>
                <button
                  type="button"
                  className="customer-portal-icon-btn customer-portal-icon-btn--pink"
                  aria-label="Remove file"
                  onClick={() => removeAdditionalFile(idx)}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {comment && (
        <div className="customer-portal-card customer-portal-new-order-section">
          <div className="customer-portal-new-order-section__title">Additional Comment</div>
          <div className="customer-portal-comment-content" dangerouslySetInnerHTML={{ __html: comment }} />
        </div>
      )}

      <div className="customer-portal-card customer-portal-new-order-section">
        <div className="customer-portal-new-order-products__header">
          <div className="customer-portal-new-order-section__title">Products</div>
          <button type="button" className="customer-portal-btn customer-portal-btn--primary" onClick={openAddProduct}>
            + Add Products
          </button>
        </div>
        <FieldError message={fieldErrors.products} />

        {products.length === 0 ? (
          <div className="customer-portal-new-order-products__empty">
            <div className="customer-portal-new-order-products__empty-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </div>
            <div className="customer-portal-new-order-products__empty-title">No Product Yet</div>
            <div className="customer-portal-new-order-products__empty-sub">Add Products to your Order</div>
          </div>
        ) : (
          <>
            <div className="customer-portal-order-view-table-wrapper">
              <table className="customer-portal-order-view-table customer-portal-order-view-table--with-actions">
                <colgroup>
                  <col />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '25px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="customer-portal-orders-full-table__center">Product</th>
                    <th className="customer-portal-orders-full-table__center">Qty</th>
                    <th className="customer-portal-orders-full-table__right">Customer Price</th>
                    <th className="customer-portal-orders-full-table__right">Total</th>
                    <th className="customer-portal-orders-full-table__center">Comments</th>
                    <th className="customer-portal-orders-full-table__center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, idx) => {
                    const hasComment = Boolean(p.comments?.trim());
                    return (
                      <tr key={idx}>
                        <td className="customer-portal-orders-full-table__center">
                          <Tooltip as="div" className="customer-portal-order-view-table__product-code">{p.productCode}</Tooltip>
                          <Tooltip as="div" className="customer-portal-order-view-table__product-name">{p.description}</Tooltip>
                        </td>
                        <Tooltip as="td" className="customer-portal-orders-full-table__center">{p.quantity}</Tooltip>
                        <Tooltip as="td" className="customer-portal-orders-full-table__right">{formatCurrency(p.unitPrice)}</Tooltip>
                        <Tooltip as="td" className="customer-portal-orders-full-table__right">{formatCurrency(p.totalPrice)}</Tooltip>
                        <td className="customer-portal-orders-full-table__center">
                          {hasComment ? (
                            <Tooltip
                              as="span"
                              className="customer-portal-order-detail__comment-icon customer-portal-order-detail__comment-icon--active"
                              content={p.comments}
                              forceOverflow
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </Tooltip>
                          ) : (
                            <span className="customer-portal-order-detail__comment-icon customer-portal-order-detail__comment-icon--inactive">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </span>
                          )}
                        </td>
                        <td className="customer-portal-order-view-table__actions">
                          <div className="customer-portal-order-view-table__actions-row">
                            <button
                              type="button"
                              className="customer-portal-icon-btn customer-portal-icon-btn--pink"
                              aria-label="Edit product"
                              onClick={() => openEditProduct(idx)}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="customer-portal-icon-btn customer-portal-icon-btn--danger"
                              aria-label="Remove product"
                              onClick={() => removeProduct(idx)}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="customer-portal-order-view-table__total">Total: {formatCurrency(productsTotal)}</div>
          </>
        )}
      </div>

      <div className="customer-portal-new-order-footer">
        <button type="button" className="customer-portal-btn customer-portal-btn--ghost" onClick={() => navigate('/customer/orders')}>
          Cancel
        </button>
        {!isPendingEdit && (
          <button
            type="button"
            className="customer-portal-btn customer-portal-btn--outline"
            disabled={saving || !customer?.id || warehousesLoading}
            onClick={() => handleSubmit(true)}
          >
            Save
          </button>
        )}
        <button
          type="button"
          className="customer-portal-btn customer-portal-btn--primary"
          disabled={saving || !customer?.id || warehousesLoading}
          onClick={() => handleSubmit(false)}
        >
          Submit
        </button>
      </div>

      {showAddProduct && (
        <AddProductModal
          customerId={customer?.id}
          catalog={productCodes}
          catalogLoading={productCodesLoading}
          onLoadCatalog={loadProductCodes}
          editingProduct={editingProductIndex != null ? products[editingProductIndex] : null}
          onCancel={closeProductModal}
          onSave={handleSaveProduct}
        />
      )}

      <Modal
        open={showCommentModal}
        title="Add Comments"
        subtitle="Add a comment for this order"
        onClose={() => setShowCommentModal(false)}
        className="customer-portal-comment-modal"
        footer={
          <>
            <button type="button" className="customer-portal-btn customer-portal-btn--ghost" onClick={() => setShowCommentModal(false)}>
              Cancel
            </button>
            <button type="button" className="customer-portal-btn customer-portal-btn--primary" onClick={saveComment}>
              Save
            </button>
          </>
        }
      >
        <div className="customer-portal-new-order-comment-modal__body">
          <RichTextEditor value={draftComment} onChange={setDraftComment} />
        </div>
      </Modal>

      <ErrorModal
        open={Boolean(apiError)}
        title={apiError?.title}
        lines={apiError?.lines}
        onClose={() => setApiError(null)}
      />

      <Modal
        open={loading || error || !customer || saving || fileActionMessage}
        onClose={() => {}}
        className="customer-portal-submit-overlay__card"
      >
        {loading ? (
          <>
            <Spinner size={32} />
            <span>{isEditMode ? 'Loading order…' : 'Loading…'}</span>
          </>
        ) : error || !customer ? (
          <>
            <WarningIcon />
            <span>{errorMessage || (isEditMode ? "Couldn't load this order" : "Couldn't load the order form")}</span>
            {!isEntityNotFound && manualRetries > 0 && (
              <button
                onClick={retry}
                className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
              >
                Retry
              </button>
            )}
          </>
        ) : (
          <>
            <Spinner size={32} />
            <span>{fileActionMessage || 'Saving your order…'}</span>
          </>
        )}
      </Modal>
    </div>
  );
}

function Crumb() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <div className="customer-portal-field-error">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="13" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {message}
    </div>
  );
}
